// Tab Operativo — distribuciones (módulo, estado, área), top abogados,
// causas estancadas, tipo de proceso.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { dashEjecOperativoJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');
const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtFecha = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? '—' : dt.toLocaleDateString('es-AR');
};

export default function TabOperativo({ onClickAbogado }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        dashEjecOperativoJWT().then(r => { if (!cancel && r?.ok) setData(r.data); }).finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, []);

    if (loading) {
        return (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}><Skeleton variant="rounded" height={300} sx={{ borderRadius: `${BR}px` }} /></Grid>
                <Grid size={{ xs: 12, md: 6 }}><Skeleton variant="rounded" height={300} sx={{ borderRadius: `${BR}px` }} /></Grid>
                <Grid size={{ xs: 12 }}><Skeleton variant="rounded" height={400} sx={{ borderRadius: `${BR}px` }} /></Grid>
            </Grid>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Por módulo (judiciales en trámite)
                        </Typography>
                        {(data.porModulo || []).length > 0 && (
                            <BarChart
                                height={260}
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: (data.porModulo || []).map(m => (m.modulo || '').slice(0, 28)) }]}
                                xAxis={[{}]}
                                series={[{ data: (data.porModulo || []).map(m => Number(m.cant || 0)), color: PRIMARY }]}
                                margin={{ top: 10, right: 20, bottom: 30, left: 200 }}
                            />
                        )}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Estados de expedientes (top 10)
                        </Typography>
                        {(data.porEstado || []).length > 0 && (
                            <PieChart
                                height={260}
                                series={[{
                                    data: (data.porEstado || []).map((e, i) => ({ id: i, value: Number(e.cant || 0), label: e.estado })),
                                    innerRadius: 50,
                                }]}
                                slotProps={{ legend: { direction: 'vertical', position: { vertical: 'middle', horizontal: 'right' }, sx: { fontSize: '0.75rem' } } }}
                                margin={{ top: 10, right: 150, bottom: 10, left: 10 }}
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>Top áreas por carga</Typography>
                        {(data.porArea || []).length > 0 && (
                            <BarChart
                                height={Math.max(260, (data.porArea || []).length * 22)}
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: (data.porArea || []).map(a => (a.area || '').slice(0, 30)) }]}
                                xAxis={[{}]}
                                series={[{ data: (data.porArea || []).map(a => Number(a.exp_count || 0)), color: '#7b1fa2' }]}
                                margin={{ top: 10, right: 20, bottom: 30, left: 220 }}
                            />
                        )}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>Top abogados (exp. activos)</Typography>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Abogado</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Mat.</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Exp.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.topAbogados || []).map((a, i) => (
                                        <TableRow key={a.idPersonal || i} hover
                                            sx={{ cursor: a.idPersonal && onClickAbogado ? 'pointer' : 'default' }}
                                            onClick={() => a.idPersonal && onClickAbogado && onClickAbogado(a.idPersonal)}>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{a.nombre}</TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{a.matricula || '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{fmtNum(a.exp_count)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Tipo de proceso */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                    Tipo de proceso (top 10 más activos)
                </Typography>
                {(data.tipoProceso || []).length > 0 ? (
                    <BarChart
                        height={260}
                        layout="horizontal"
                        yAxis={[{ scaleType: 'band', data: (data.tipoProceso || []).map(t => (t.tipo || '').slice(0, 35)) }]}
                        xAxis={[{}]}
                        series={[{ data: (data.tipoProceso || []).map(t => Number(t.cant || 0)), color: '#0277bd' }]}
                        margin={{ top: 10, right: 20, bottom: 30, left: 250 }}
                    />
                ) : <Typography color="text.secondary">Sin datos</Typography>}
            </Paper>

            {/* Causas que pueden prescribir — alerta crítica */}
            {(data.prescribibles?.riesgo4a5 > 0 || data.prescribibles?.riesgo5mas > 0) && (
                <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2, border: '2px solid #c62828', background: '#ffebee' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#c62828' }}>
                        🚨 Causas que pueden prescribir (riesgo de pérdida de cobro)
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 1.5 }}>
                        <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: `${BR}px`, border: '1px solid #ffb74d' }}>
                                <Typography variant="caption" sx={{ color: '#e65100', textTransform: 'uppercase', fontWeight: 700 }}>
                                    Atención (4-5 años sin movimiento)
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>
                                    {fmtNum(data.prescribibles.riesgo4a5)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: `${BR}px`, border: '2px solid #c62828' }}>
                                <Typography variant="caption" sx={{ color: '#c62828', textTransform: 'uppercase', fontWeight: 700 }}>
                                    Crítico (5+ años sin movimiento)
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                                    {fmtNum(data.prescribibles.riesgo5mas)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                        Top 30 más urgentes (ordenadas por años sin movimiento, luego monto):
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Expediente</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Carátula</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Módulo</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Último mov.</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Años</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Monto</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.prescribibles.listado || []).map((e, i) => (
                                    <TableRow key={i} hover sx={{ cursor: 'pointer',
                                        bgcolor: Number(e.anios_sin_mov) >= 5 ? '#ffcdd2' : 'transparent' }}
                                        onClick={() => window.open(`/expediente/judicial/${e.Id}`, '_blank', 'noopener,noreferrer')}>
                                        <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{e.Id}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {e.Caratula}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem' }}>{e.Modulo}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem' }}>{fmtFecha(e.ultimo_mov)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700, color: Number(e.anios_sin_mov) >= 5 ? '#c62828' : '#e65100' }}>
                                            {e.anios_sin_mov}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{fmtMoney(e.monto)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Causas estancadas */}
            {(data.causasEstancadas || []).length > 0 && (
                <Paper sx={{ p: 2, borderRadius: `${BR}px`, border: '1px solid #ffb74d' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#e65100' }}>
                        ⚠️ Top 20 causas estancadas (sin movimientos en más de 90 días)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Expediente</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Carátula</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Módulo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Días sin mov.</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.causasEstancadas || []).map((e, i) => {
                                    const tipoUrl = Number(e.tipo) === 2 ? 'administrativo' : 'judicial';
                                    const tipoLabel = Number(e.tipo) === 2 ? 'Adm.' : 'Jud.';
                                    return (
                                        <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                                            onClick={() => window.open(`/expediente/${tipoUrl}/${e.Id}`, '_blank', 'noopener,noreferrer')}>
                                            <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{e.Id}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', color: tipoUrl === 'administrativo' ? '#e65100' : '#1565c0', fontWeight: 600 }}>
                                                {tipoLabel}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {e.Caratula}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem' }}>{e.Modulo}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#c62828' }}>{fmtNum(e.dias_sin_mov)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}
